import React, { useEffect, useState } from "react";
import { Plus, Pencil } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState } from "@/components/ListState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export const SettingsCategories = () => {
  const { canWrite } = useAuth();
  const writable = canWrite();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", default_lifetime_months: "", default_lifetime_usage: "" });
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/asset-categories");
      setItems(data);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Categorieën konden niet geladen worden."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", default_lifetime_months: "", default_lifetime_usage: "" }); setError(""); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      default_lifetime_months: c.default_lifetime_months ?? "",
      default_lifetime_usage: c.default_lifetime_usage ?? "",
    });
    setError("");
    setOpen(true);
  };

  const numOrNull = (v) => (v === "" ? null : Number(v));

  const submit = async () => {
    setError("");
    const body = {
      name: form.name,
      default_lifetime_months: numOrNull(form.default_lifetime_months),
      default_lifetime_usage: numOrNull(form.default_lifetime_usage),
    };
    try {
      if (editing) {
        await api.put(`/asset-categories/${editing.category_id}`, body);
        toast.success("Categorie bijgewerkt");
      } else {
        await api.post("/asset-categories", body);
        toast.success("Categorie aangemaakt");
      }
      setOpen(false);
      await load();
    } catch (err) {
      setError(getErrorMessage(err, "Opslaan mislukt."));
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/asset-categories/${c.category_id}`, { active: !c.active });
      toast.success(c.active ? "Categorie gedeactiveerd" : "Categorie geactiveerd");
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Actie mislukt."));
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-body text-muted-foreground">{items.length} categorieën</p>
        {writable && (
          <Button data-testid="categories-create-button" onClick={openCreate}>
            <Plus size={18} className="mr-2" /> Categorie toevoegen
          </Button>
        )}
      </div>

      <Card>
        {loadError ? (
          <ErrorState testid="categories-load-error" message={loadError} onRetry={load} className="border-0" />
        ) : (
        <>
        <div className="hidden sm:block">
          <Table data-testid="categories-table">
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Standaard levensduur (mnd)</TableHead>
                <TableHead>Standaard gebruik</TableHead>
                <TableHead>Actief</TableHead>
                <TableHead className="w-[80px] text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="py-8 text-center text-body text-muted-foreground">Laden...</TableCell></TableRow>}
              {!loading && items.map((c) => (
                <TableRow key={c.category_id} data-testid={`category-row-${c.category_id}`} className={c.active ? "" : "opacity-60"}>
                  <TableCell className="font-medium text-foreground">{c.name}</TableCell>
                  <TableCell className="font-mono text-data text-muted-foreground">{c.category_id}</TableCell>
                  <TableCell className="tabular-nums">{c.default_lifetime_months}</TableCell>
                  <TableCell className="tabular-nums">{c.default_lifetime_usage}</TableCell>
                  <TableCell>
                    <Switch data-testid={`category-active-switch-${c.category_id}`} checked={c.active} disabled={!writable} onCheckedChange={() => toggleActive(c)} />
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      {writable ? (
                        <Button variant="ghost" size="icon" data-testid={`category-edit-button-${c.category_id}`} onClick={() => openEdit(c)} title="Bewerken"><Pencil size={18} /></Button>
                      ) : <span className="text-data text-muted-foreground">Geen rechten</span>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Phone: one card per categorie. */}
        <div className="divide-y divide-border sm:hidden" data-testid="categories-cardlist">
          {loading && <p className="py-6 text-center text-body text-muted-foreground">Laden...</p>}
          {!loading && items.map((c) => (
            <div key={c.category_id} data-testid={`category-card-${c.category_id}`} className={`flex items-start justify-between gap-3 p-3 ${c.active ? "" : "opacity-60"}`}>
              <div className="min-w-0 space-y-0.5">
                <div className="font-medium text-foreground">{c.name}</div>
                <div className="font-mono text-data text-muted-foreground">{c.category_id}</div>
                <div className="text-data text-muted-foreground">
                  {[c.default_lifetime_months ? `${c.default_lifetime_months} mnd` : null,
                    c.default_lifetime_usage ? `${c.default_lifetime_usage} gebruik` : null].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Switch data-testid={`category-card-active-switch-${c.category_id}`} checked={c.active} disabled={!writable} onCheckedChange={() => toggleActive(c)} />
                {writable && (
                  <Button variant="ghost" size="icon" data-testid={`category-card-edit-button-${c.category_id}`} onClick={() => openEdit(c)} title="Bewerken"><Pencil size={18} /></Button>
                )}
              </div>
            </div>
          ))}
        </div>
        </>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent srDescription="Naam en standaard levensduur van deze assetcategorie.">
          <DialogHeader><DialogTitle>{editing ? "Categorie bewerken" : "Categorie toevoegen"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Naam</Label><Input data-testid="category-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Standaard levensduur (maanden)</Label><Input type="number" data-testid="category-lifetime-input" value={form.default_lifetime_months} onChange={(e) => setForm({ ...form, default_lifetime_months: e.target.value })} placeholder="Leeg laten kan" /></div>
            <div className="space-y-1.5"><Label>Standaard levensduur (gebruik)</Label><Input type="number" value={form.default_lifetime_usage} onChange={(e) => setForm({ ...form, default_lifetime_usage: e.target.value })} placeholder="Optioneel" /></div>
            {error && <p className="text-data text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button data-testid="category-submit-button" onClick={submit}>Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
